import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import { Bot, Building2, KeyRound, Mail } from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { isAuthenticated } from '../../store/selectors/AuthSelectors';
import AccountPage from '../layouts/AccountPage';
import Callout from '../components/Common/Callout';

export const scopeLabels = {
    'mcp:companies': 'Visa namn och ID för valda företag',
    'mcp:read': 'Läsa uppgifter som du har behörighet att se',
    'mcp:write': 'Utföra åtgärder som du har behörighet att göra',
};
const scopeTitles = { 'mcp:companies': 'Företagslista', 'mcp:read': 'Läsa', 'mcp:write': 'Utföra åtgärder' };

// A checkbox row with a title and an optional description. The <label> text
// is the title alone so the row can be found by its name.
function Choice({ id, checked, disabled, onChange, title, description }) {
    return <label htmlFor={id} className={`ll-choice${checked ? ' is-checked' : ''}${disabled ? ' is-disabled' : ''}`}>
        <input type="checkbox" id={id} checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} aria-describedby={description ? `${id}-desc` : undefined} />
        <span className="ll-choice__text">
            <span className="ll-choice__title">{title}</span>
            {description && <span className="ll-choice__desc" id={`${id}-desc`} aria-hidden="true">{description}</span>}
        </span>
    </label>;
}

export default function McpConsent() {
    const authenticated = useSelector(isAuthenticated);
    const location = useLocation();
    const transaction = new URLSearchParams(location.search).get('transaction');
    const [preview, setPreview] = useState(null), [companies, setCompanies] = useState([]), [scopes, setScopes] = useState([]);
    const [error, setError] = useState(''), [recent, setRecent] = useState(false), [busy, setBusy] = useState(false);
    const generation = useRef(0);
    useEffect(() => {
        let active = true;
        generation.current += 1;
        const cleanup = () => { active = false; generation.current += 1; };
        setPreview(null); setCompanies([]); setScopes([]); setError('');
        if (!authenticated) return cleanup;
        if (!/^llm_transaction_[A-Za-z0-9_-]{43}$/.test(transaction || '')) {
            setError('Anslutningslänken är ogiltig. Börja om från din AI-app.'); return cleanup;
        }
        setBusy(true);
        axios.post(getApiUrl('auth/mcp/preview'), { transaction }).then(({ data }) => {
            if (active) { setPreview(data); setScopes(data.scopes); }
        }).catch(() => { if (active) setError('Anslutningen kunde inte hämtas. Försök igen eller börja om från din AI-app.'); })
            .finally(() => { if (active) setBusy(false); });
        return cleanup;
    }, [transaction, authenticated]);
    if (!authenticated) return <Redirect to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />;
    const toggle = (setter, value, checked) => setter(previous => checked ? [...previous, value] : previous.filter(item => item !== value));
    const decide = async decision => {
        const current = generation.current;
        setBusy(true); setError(''); setRecent(false);
        try {
            const { data } = await axios.post(getApiUrl('auth/mcp/consent'), { transaction, decision, companies, scopes });
            if (current !== generation.current) return;
            const target = new URL(data.completeUrl);
            if (!['https:', 'http:'].includes(target.protocol) || target.pathname !== '/oauth/complete') throw new Error();
            window.location.assign(target.href);
        } catch (failure) {
            if (current !== generation.current) return;
            const needsLogin = failure?.response?.data?.code === 'RECENT_LOGIN_REQUIRED';
            setRecent(needsLogin);
            setError(needsLogin ? 'Logga in igen för att godkänna anslutningen.' : 'Anslutningen kunde inte slutföras. Börja om från din AI-app.');
            setBusy(false);
        }
    };
    const loginAgain = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    return <AccountPage width="narrow" back={{ to: '/connected-apps', label: 'Anslutna appar' }}>
        <div className="modern-card ll-consent">
            <div className="modern-card-body">
                <div className="ll-consent__hero">
                    <span className="ll-consent__glyph"><Bot size={28} strokeWidth={1.75} aria-hidden="true" /></span>
                    <h1 className="ll-consent__title">Anslut en AI-app</h1>
                    {preview
                        ? <><p className="ll-consent__lead"><strong>{preview.client.name}</strong> vill ansluta till ditt LedgerLoop-konto.</p>
                            <span className="ll-consent__account"><Mail size={13} aria-hidden="true" />{preview.accountEmail}</span></>
                        : <p className="ll-consent__lead">Du bestämmer vilka företag appen får se och vad den får göra.</p>}
                </div>
                {busy && !preview && !error && <p role="status" className="text-center text-muted"><Spinner as="span" animation="border" size="sm" className="me-2" />Hämtar anslutningen…</p>}
                {error && <Callout tone="danger" role="alert">
                    <p>{error}</p>
                    {recent && <p><Link to={loginAgain} className="btn btn-sm btn-outline-primary mt-1">Logga in igen</Link></p>}
                </Callout>}
                {preview && <>
                    <Callout tone="warning">
                        <p>Appens namn anges av den som registrerat appen. Fortsätt bara om du känner igen adressen nedan och själv startade anslutningen.</p>
                        <p>Återvändningsadress: <code className="ll-code">{preview.client.redirectUri}</code></p>
                    </Callout>
                    <section className="ll-consent__section" aria-labelledby="consent-companies">
                        <h2 className="ll-consent__heading" id="consent-companies"><Building2 size={12} aria-hidden="true" /> Välj företag</h2>
                        {!preview.companies.length && <Callout tone="info"><p>Du behöver tillgång till ett företag innan du kan ansluta appen.</p></Callout>}
                        {preview.companies.map(company => <Choice key={company.companyId} id={`company-${company.companyId}`} title={company.companyName}
                            checked={companies.includes(company.companyId)} disabled={busy} onChange={checked => toggle(setCompanies, company.companyId, checked)} />)}
                    </section>
                    <section className="ll-consent__section" aria-labelledby="consent-scopes">
                        <h2 className="ll-consent__heading" id="consent-scopes"><KeyRound size={12} aria-hidden="true" /> Välj åtkomst</h2>
                        {preview.scopes.map(scope => <Choice key={scope} id={`scope-${scope}`} title={scopeTitles[scope] || scope} description={scopeLabels[scope]}
                            checked={scopes.includes(scope)} disabled={busy} onChange={checked => toggle(setScopes, scope, checked)} />)}
                    </section>
                    <Callout tone="info">
                        <p>Appen får aldrig större behörighet än du själv har, och nya företag eller behörigheter läggs inte till automatiskt. Du kan koppla från appen när som helst under Anslutna appar.</p>
                    </Callout>
                    <div className="ll-consent__actions">
                        <Button variant="outline-secondary" disabled={busy} onClick={() => decide('deny')}>Avböj</Button>
                        <Button variant="primary" disabled={busy || !companies.length || !scopes.length} onClick={() => decide('approve')}>Godkänn anslutning</Button>
                    </div>
                </>}
            </div>
        </div>
        <p className="ll-consent__footer"><Link to="/connected-apps">Hantera anslutna appar</Link></p>
    </AccountPage>;
}
